import { useCallback, useEffect, useRef, useState } from "react";
import { useWebStoreContext } from "contexts/WebStoreProvider";
import { useEnvContext } from "contexts/EnvProvider";
import { useGoogleAnalyticsContext } from "contexts/GoogleAnalyticsProvider";
import { FeaturesFlags } from "helpers";
import { getIsStandalonePWA } from "../helpers/getIsStandalonePWA";
import {
	isPlaygamiPushAvailable,
	getPlaygamiSystemPermission,
	getPlaygamiNotificationsEnabled,
	subscribePlaygamiPush,
	startPlaygamiPush,
	unsubscribePlaygamiPush
} from "services/push/playgamiPushService";
import { usePushSubscriptionPWAMutation } from "redux/apis/pwa";
import { PUSH_LOCAL_STATE_KEY, PushLocalState } from "interfaces/pwa";
import createPersistedState from "use-persisted-state";
import { getPlatform } from "utils/platform";

export type PushNotifications = {
	isSupported: boolean;
	isSubscribed: boolean;
	permission: NotificationPermission | "unsupported";
	subscribe: () => Promise<void>;
	refresh: () => void;
	pushLocalState: PushLocalState | null | undefined;
	setPushLocalState: (state: PushLocalState | null) => void;
};

export const usePushNotifications = (): PushNotifications => {
	const { userTracker } = useWebStoreContext();
	const { isFeatureFlagEnabled } = useEnvContext();
	const { reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const isPushEnabled = isFeatureFlagEnabled(FeaturesFlags.PUSH_NOTIFICATIONS);

	const usePushLocalState = createPersistedState<PushLocalState | null>(
		PUSH_LOCAL_STATE_KEY
	);

	const [pushLocalState, setPushLocalState] = usePushLocalState();

	const [isSubscribed, setIsSubscribed] = useState(false);
	const [permission, setPermission] = useState<
		NotificationPermission | "unsupported"
	>(
		typeof Notification === "undefined"
			? "unsupported"
			: Notification.permission
	);

	const [isSupported, setIsSupported] = useState(false);
	const [sendPushSubscription] = usePushSubscriptionPWAMutation();
	const previousIsSubscribedRef = useRef<boolean | null>(null);

	const refresh = useCallback(() => {
		if (!isPushEnabled) {
			setIsSupported(false);
			setPermission("unsupported");
			setIsSubscribed(false);
			return;
		}

		const available = isPlaygamiPushAvailable();
		const sdkPermission = getPlaygamiSystemPermission();
		const browserPermission: NotificationPermission | "unsupported" =
			typeof Notification === "undefined"
				? "unsupported"
				: Notification.permission;
		const notificationsEnabled = getPlaygamiNotificationsEnabled();
		const standalone = getIsStandalonePWA();

		setIsSupported(available && standalone);
		setPermission(
			browserPermission === "unsupported" ? sdkPermission : browserPermission
		);
		setIsSubscribed(notificationsEnabled);
	}, [isPushEnabled]);

	// Initial capability / state detection
	useEffect(() => {
		refresh();
	}, [refresh]);

	// Re-sync permission/subscription when the app returns to foreground.
	useEffect(() => {
		if (typeof window === "undefined" || typeof document === "undefined") {
			return;
		}

		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				refresh();
			}
		};

		window.addEventListener("focus", refresh);
		window.addEventListener("pageshow", refresh);
		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			window.removeEventListener("focus", refresh);
			window.removeEventListener("pageshow", refresh);
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, [refresh]);

	// When the feature flag is disabled, unsubscribe from SDK (if subscribed)
	// and clear persisted push state. setPushLocalState is intentionally omitted
	// from deps: use-persisted-state's setter is unstable (changes on every
	// value change) and both operations only need to react to isPushEnabled.
	useEffect(() => {
		if (!isPushEnabled) {
			if (getPlaygamiNotificationsEnabled()) {
				void unsubscribePlaygamiPush().catch(() => undefined);
			}
			setPushLocalState(null);
		}
	}, [isPushEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

	// Report subscription status on app open and on status changes.
	useEffect(() => {
		if (!isPushEnabled || !isSupported) {
			previousIsSubscribedRef.current = null;
			return;
		}

		const previousIsSubscribed = previousIsSubscribedRef.current;
		if (previousIsSubscribed === null) {
			previousIsSubscribedRef.current = isSubscribed;
			reportPlaygamiEvent("tp.web_frontend", {
				event_name: "pwa_subscribe_status_update",
				event_type: "pwa_subscribe_status",
				action: "updated",
				source: "pwa_push",
				type: isSubscribed ? "yes" : "no",
				page_location: window.location.pathname
			});
			return;
		}

		if (previousIsSubscribed === isSubscribed) {
			return;
		}

		previousIsSubscribedRef.current = isSubscribed;
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "pwa_subscribe_status_update",
			event_type: "pwa_subscribe_status",
			action: "updated",
			source: "pwa_push",
			type: isSubscribed ? "yes" : "no",
			page_location: window.location.pathname
		});
	}, [isPushEnabled, isSupported, isSubscribed, reportPlaygamiEvent]);

	// Detect reinstall/reset on Android: if native permission is "default" (never asked)
	// but local state says push was ever enabled, it means the app was reinstalled and
	// permission state reset. In this case, clear local state to re-show the popup.
	useEffect(() => {
		if (!isPushEnabled || !pushLocalState?.everEnabled) {
			return;
		}

		if (getPlatform() !== "android") {
			return;
		}

		if (permission === "default") {
			// console.log(
			// 	"[Push] Detected possible reinstall: permission reset to default but everEnabled=true, clearing local state"
			// );
			setPushLocalState(null);
		}
	}, [isPushEnabled, permission, pushLocalState?.everEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

	// Keep Playgami subscription in sync when we have a user and PWA is running
	useEffect(() => {
		if (!isPushEnabled) {
			// console.log(
			// 	"[Push] Push notifications disabled, skipping startPlaygamiPush"
			// );
			return;
		}

		if (getPlatform() === "ios") {
			// console.log("[Push] iOS detected, skipping automatic startPlaygamiPush");
			return;
		}

		if (!userTracker?.id) {
			// console.log("[Push] No user ID, skipping startPlaygamiPush");
			return;
		}

		if (!isPlaygamiPushAvailable()) {
			// console.log(
			// 	"[Push] Playgami push not available, skipping startPlaygamiPush"
			// );
			return;
		}

		if (!getIsStandalonePWA()) {
			// console.log("[Push] Not a PWA, skipping startPlaygamiPush");
			return;
		}

		// Fire and forget; errors are logged inside the service if needed
		// console.log("[Push] Calling startPlaygamiPush");
		void startPlaygamiPush(userTracker.id);
	}, [isPushEnabled, userTracker?.id]);

	const subscribe = useCallback(async () => {
		if (!isPushEnabled) {
			return;
		}

		if (!isPlaygamiPushAvailable()) {
			return;
		}

		if (!getIsStandalonePWA()) {
			return;
		}

		if (!userTracker?.id) {
			return;
		}

		try {
			await subscribePlaygamiPush(userTracker.id);
			// Re-sync local state from Playgami after subscribing
			refresh();
			// After subscribing with Playgami, forward the full subscription to our backend
			try {
				const registration = await navigator.serviceWorker.ready;
				const subscription = await registration.pushManager.getSubscription();
				// console.log("[Push] Registration and subscription from browser", {
				// 	registration,
				// 	subscription
				// });
				if (subscription) {
					// console.log("[Push] Sending subscription to backend", subscription);
					await sendPushSubscription(subscription).unwrap();
				}
			} catch (sendError) {
				// eslint-disable-next-line no-console
				// console.log("[Push] Error sending subscription to backend", sendError);
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			// console.log("[Push] Error during Playgami subscribe", error);
			// Even on error, re-sync in case anything changed
			refresh();
		}
	}, [isPushEnabled, userTracker?.id, refresh, sendPushSubscription]);

	// Lightweight debug: surface the current push hook state

	return {
		isSupported,
		isSubscribed,
		permission,
		subscribe,
		refresh,
		pushLocalState,
		setPushLocalState
	};
};
